import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { getIn, useFormikContext } from "formik";
import { FieldLabel } from "react-invenio-forms";
import { Form } from "semantic-ui-react";

const EMPTY_VALUE = { id: "", schema: "", url: "", label: "" };

const SUBFIELD_ROWS = [
  [
    { key: "id", label: "ID", placeholder: "Vocabulary item identifier" },
    { key: "label", label: "Label", placeholder: "Human-readable name" },
  ],
  [
    { key: "schema", label: "Schema", placeholder: "Vocabulary namespace URI" },
    {
      key: "url",
      label: "URL",
      placeholder: "https://example.com/vocabulary/item",
    },
  ],
];

const ALL_TOUCHED = { id: true, label: true, schema: true, url: true };
const NO_TOUCHED = { id: false, label: false, schema: false, url: false };

const getSubfieldError = (key, val) => {
  if (!val || !val.trim()) return "Required";
  if (key === "url") {
    try {
      new URL(val);
    } catch {
      return "Must be a valid URL (e.g. https://example.com)";
    }
  }
  return null;
};

const ExternalVocabularyInput = ({
  fieldPath,
  label,
  icon,
  required,
  description,
}) => {
  const { values, setFieldValue, submitCount } = useFormikContext();
  const [touched, setTouched] = useState(NO_TOUCHED);

  const storedValue = getIn(values, fieldPath, null);
  const currentValue =
    storedValue && typeof storedValue === "object" ? storedValue : EMPTY_VALUE;

  const hasAnyValue = !!(
    currentValue.id ||
    currentValue.label ||
    currentValue.schema ||
    currentValue.url
  );

  // When all sub-fields are cleared, reset touched so stale errors don't linger.
  const effectiveTouched = hasAnyValue ? touched : NO_TOUCHED;

  // On submit attempt, reveal all errors if the field has been partially filled.
  useEffect(() => {
    if (submitCount > 0 && hasAnyValue) {
      setTouched(ALL_TOUCHED);
    }
  }, [submitCount, hasAnyValue]);

  const handleChange = (key, newVal) => {
    const updated = { ...currentValue, [key]: newVal };
    const allEmpty =
      !updated.id && !updated.schema && !updated.url && !updated.label;
    setFieldValue(fieldPath, allEmpty ? null : updated);
  };

  const handleBlur = (key) => {
    setTouched((prev) => ({ ...prev, [key]: true }));
  };

  return (
    <Form.Field required={required}>
      <FieldLabel icon={icon} label={label} />
      {SUBFIELD_ROWS.map((row, rowIdx) => (
        <Form.Group key={rowIdx} widths="equal">
          {row.map(({ key, label: subLabel, placeholder }) => {
            const error =
              effectiveTouched[key] || hasAnyValue
                ? getSubfieldError(key, currentValue[key])
                : null;
            return (
              <Form.Input
                key={key}
                id={`${fieldPath}.${key}`}
                label={subLabel}
                required
                value={currentValue[key] || ""}
                placeholder={placeholder}
                error={error}
                onChange={(e) => handleChange(key, e.target.value)}
                onBlur={() => handleBlur(key)}
                fluid
              />
            );
          })}
        </Form.Group>
      ))}
      {description && <label className="helptext">{description}</label>}
    </Form.Field>
  );
};

ExternalVocabularyInput.propTypes = {
  fieldPath: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  icon: PropTypes.string,
  required: PropTypes.bool,
  description: PropTypes.string,
};

ExternalVocabularyInput.defaultProps = {
  icon: undefined,
  required: false,
  description: undefined,
};

export default ExternalVocabularyInput;
